import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const QUICK_FIND_CATEGORIES = ["eat", "drink", "fun"];
const inFlightQuickFindSeedRequests = new Map();

function normalizeCenter(center) {
  const lat = Number(center?.lat);
  const lng = Number(center?.lng);
  return {
    lat: Number.isFinite(lat) ? Number(lat.toFixed(6)) : null,
    lng: Number.isFinite(lng) ? Number(lng.toFixed(6)) : null,
  };
}

function normalizeCategories(categories) {
  const source = Array.isArray(categories) && categories.length
    ? categories
    : QUICK_FIND_CATEGORIES;
  const requested = new Set(
    source
      .map((category) => String(category || "").trim().toLowerCase())
      .filter((category) => QUICK_FIND_CATEGORIES.includes(category))
  );
  return QUICK_FIND_CATEGORIES.filter((category) => requested.has(category));
}

function normalizeCategoryResult(value) {
  const capsules = Array.isArray(value) ? value : [];
  const capsuleIds = Array.from(
    new Set(
      capsules
        .map((capsule) => String(capsule?.id || capsule?.capsuleId || "").trim())
        .filter(Boolean)
    )
  );
  return {
    capsules,
    capsuleIds,
    count: capsuleIds.length,
  };
}

function normalizeQuickFindSeedResponse(data = {}) {
  const categories = QUICK_FIND_CATEGORIES.reduce((acc, category) => {
    acc[category] = normalizeCategoryResult(data?.categories?.[category]);
    return acc;
  }, {});
  const lat = Number(data?.center?.lat);
  const lng = Number(data?.center?.lng);
  return {
    categories,
    locationKey: String(data?.locationKey || ""),
    center:
      Number.isFinite(lat) && Number.isFinite(lng)
        ? { lat, lng }
        : null,
    radiusMeters: Number(data?.radiusMeters || 0) || 0,
    expandedRadiusMeters: Number(data?.expandedRadiusMeters || 0) || 0,
    minResultsPerCategory: Number(data?.minResultsPerCategory || 0) || 0,
    createdCapsuleIds: Array.isArray(data?.createdCapsuleIds) ? data.createdCapsuleIds : [],
    reusedCapsuleIds: Array.isArray(data?.reusedCapsuleIds) ? data.reusedCapsuleIds : [],
    errors: Array.isArray(data?.errors) ? data.errors : [],
  };
}

function buildQuickFindSeedRequestKey(payload) {
  return JSON.stringify({
    center: normalizeCenter(payload.center),
    placeId: String(payload.placeId || "").trim(),
    categories: normalizeCategories(payload.categories),
  });
}

export async function seedQuickFindCategories({
  center,
  placeId = "",
  categories = QUICK_FIND_CATEGORIES,
} = {}) {
  const callable = httpsCallable(functions, "discover_seedQuickFindCategories");
  const normalizedCategories = normalizeCategories(categories);
  const payload = {
    center,
    categories: normalizedCategories,
  };
  const normalizedPlaceId = String(placeId || "").trim();
  if (normalizedPlaceId) payload.placeId = normalizedPlaceId;

  const requestKey = buildQuickFindSeedRequestKey(payload);
  const existingRequest = inFlightQuickFindSeedRequests.get(requestKey);
  if (existingRequest) return existingRequest;

  const request = callable(payload)
    .then((response) => normalizeQuickFindSeedResponse(response?.data || {}))
    .finally(() => {
      inFlightQuickFindSeedRequests.delete(requestKey);
    });
  inFlightQuickFindSeedRequests.set(requestKey, request);
  return request;
}

export {
  QUICK_FIND_CATEGORIES,
  normalizeQuickFindSeedResponse,
  buildQuickFindSeedRequestKey,
};
